"use client";

/**
 * ABAC (Attribute-Based Access Control) for Next.js 15 / React 19
 *
 * Based on: https://github.com/rikhoffbauer/react-abac
 *
 * @example
 * ```tsx
 * // Define your types
 * type Role = 'admin' | 'user' | 'guest';
 * type Permission = 'read' | 'write' | 'delete';
 * type User = { id: string; name: string };
 *
 * // Create your ABAC instance
 * const { AbacProvider, AllowedTo, NotAllowedTo, useAbac, secured } = create<Role, Permission, User>();
 *
 * // Use in your app
 * <AbacProvider rules={rules} user={user} roles={['admin']}>
 *   <AllowedTo perform="write">
 *     <button>Edit</button>
 *   </AllowedTo>
 * </AbacProvider>
 * ```
 */

import create, { type AbacContextProps, type AbacProviderProps, type AllowedToProps, type SecuredOptions } from "./create";

export { default as ensureArray } from "./utils/ensureArray";
export { default as evaluateRule } from "./utils/evaluateRule";
export { default as roleHasPermission } from "./utils/roleHasPermission";
export { default as rolesHavePermissions } from "./utils/rolesHavePermissions";

export type { Rules } from "./interfaces";
export type { AbacContextProps, SecuredOptions, AllowedToProps, AbacProviderProps } from "./create";
export { default as create } from "./create";

const abac = create();

export const { AllowedTo, secured, NotAllowedTo, AbacContextDefaults, AbacProvider, AbacContext, useAbac } = abac;
